import { type Reference, gql } from "@apollo/client/core";
import {
  type ActionListActionFieldsFragment,
  ActionListActionFieldsFragmentDoc,
  type EABEventDataFragment,
  EABEventDataFragmentDoc,
  type EABLoadBoardEventsQuery,
  type EventListEventFieldsFragment,
  EventListEventFieldsFragmentDoc,
  SQDCGlobalSettingsDocument,
  type SQDCGlobalSettingsQuery,
} from "@/graphql";
import cache from "./apolloCache";

export function loadCachedSQDCGlobalSettings() {
  const result = cache.readQuery<SQDCGlobalSettingsQuery>({
    query: SQDCGlobalSettingsDocument,
  });
  return result?.SQDCGlobalSettings;
}

export function addEvent(event: EventListEventFieldsFragment, boardId: number) {
  cache.modify({
    id: "ROOT_QUERY",
    fields: {
      eventsForBoard(oldEventsForBoard, { storeFieldName }) {
        const boardIdRegex = /eventsForBoard\(.+:(\d+)\}\)/g;

        const boardIdFromQuery = boardIdRegex.exec(storeFieldName)?.[1];
        if (boardIdFromQuery != null && Number(boardIdFromQuery) === boardId) {
          const newEventRef = cache.writeFragment({
            data: event,
            fragment: EventListEventFieldsFragmentDoc,
            fragmentName: "EventListEventFields",
          });

          const ordering = loadCachedSQDCGlobalSettings();

          if (ordering?.eventOrdering === "ASC") {
            return [...oldEventsForBoard, newEventRef];
          }
          return [newEventRef, ...oldEventsForBoard];
        }
        return oldEventsForBoard;
      },
    },
  });
}

export function addAction(storedObject: string, action: ActionListActionFieldsFragment) {
  cache.modify({
    id: storedObject,
    fields: {
      actions(oldActionRefs) {
        const newActionRef = cache.writeFragment({
          data: action,
          fragment: ActionListActionFieldsFragmentDoc,
          fragmentName: "ActionListActionFields",
        });

        const ordering = loadCachedSQDCGlobalSettings();
        if (ordering?.actionOrdering === "DEADLINE") {
          const actions: { ref: Reference | undefined; deadline: number }[] = oldActionRefs.map(
            (ref: Reference) => {
              const cacheAction = cache.readFragment<{ deadline: string }>({
                id: ref.__ref,
                fragment: gql`
                  fragment ActionDeadlineFragment on Action {
                    deadline
                  }
                `,
                fragmentName: "ActionDeadlineFragment",
              });

              return {
                ref,
                deadline:
                  cacheAction == null
                    ? new Date().valueOf()
                    : new Date(cacheAction?.deadline).valueOf(),
              };
            },
          );
          actions.push({
            ref: newActionRef,
            deadline: new Date(action.deadline).valueOf(),
          });
          return actions
            .sort((a, b) => new Date(a.deadline).valueOf() - new Date(b.deadline).valueOf())
            .map((el) => el.ref);
        }
        if (ordering?.actionOrdering === "ASC") {
          return [...oldActionRefs, newActionRef];
        }
        return [newActionRef, ...oldActionRefs];
      },
    },
  });
}

type SQDCBoardEvent = EABLoadBoardEventsQuery["SQDCEvents"][number];
type SQDCBoardAction = NonNullable<SQDCBoardEvent["ActionEventConnection"][number]["action"]>;

function sqdcEventsStoreFieldMatchesBoard(storeFieldName: string, boardId: number) {
  return storeFieldName.includes(`"boardIds":[${boardId}]`);
}

export function addSQDCEvent(event: EABEventDataFragment, boardId: number) {
  cache.modify({
    id: "ROOT_QUERY",
    fields: {
      SQDCEvents(oldEvents = [], { storeFieldName }) {
        if (!Array.isArray(oldEvents)) {
          return oldEvents;
        }

        if (!sqdcEventsStoreFieldMatchesBoard(storeFieldName, boardId)) {
          return oldEvents;
        }

        const newEventRef = cache.writeFragment({
          data: event,
          fragment: EABEventDataFragmentDoc,
          fragmentName: "EABEventData",
        });

        if (newEventRef == null) {
          return oldEvents;
        }

        return [
          newEventRef,
          ...oldEvents.filter((eventRef) => eventRef.__ref !== newEventRef.__ref),
        ];
      },
    },
  });
}

export function removeSQDCEvent(eventId: number, boardId: number) {
  cache.modify({
    id: "ROOT_QUERY",
    fields: {
      SQDCEvents(oldEvents = [], { readField, storeFieldName }) {
        if (!Array.isArray(oldEvents)) {
          return oldEvents;
        }
        if (!sqdcEventsStoreFieldMatchesBoard(storeFieldName, boardId)) {
          return oldEvents;
        }
        return oldEvents.filter((eventRef) => readField<number>("id", eventRef) !== eventId);
      },
    },
  });
}

export function addSQDCAction(eventId: number, action: SQDCBoardAction) {
  const eventCacheId = cache.identify({
    __typename: "SQDCEvent",
    id: eventId,
  });

  if (eventCacheId == null) {
    return;
  }

  const actionRef = cache.writeFragment({
    data: action,
    fragment: gql`
      fragment SQDCActionCacheFields on SQDCAction {
        id
      }
    `,
    fragmentName: "SQDCActionCacheFields",
  });

  cache.modify({
    id: eventCacheId,
    fields: {
      updatedAt() {
        return action.updatedAt;
      },
      ActionEventConnection(oldConnections = []) {
        if (!Array.isArray(oldConnections)) {
          return oldConnections;
        }

        if (actionRef == null) {
          return oldConnections;
        }

        return [
          ...oldConnections.filter((connection) => connection.action?.__ref !== actionRef?.__ref),
          {
            __typename: "SQDCActionEventConnection",
            action: actionRef,
          },
        ];
      },
    },
  });
}
